import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Store, Users, Eye, Video, Calendar, Search, Clock, Lock, Globe, Send, Download, FileSpreadsheet, ShoppingCart } from 'lucide-react';
import AutoTranslatedText from '../AutoTranslatedText';
import OptimizedImage from '../OptimizedImage';

const BuyerExhibitorsTab = ({
  selectedEvent,
  myRegistrations,
  filteredExhibitors,
  searchTerm,
  setSearchTerm,
  listPricing,
  downloadExhibitorList,
  handlePurchaseExhibitorList,
  setSelectedBooth,
  setShowBoothModal,
  setInteractionType,
  setShowInteractionModal,
  setMessageBooth,
  setShowMessageModal,
  setActiveTab,
  formatPrice,
  t,
  events,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('dashboard.exhibitors')}</CardTitle>
            <CardDescription>
              {selectedEvent ? `${selectedEvent.name}` : t('dashboard.register_events_desc')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Download Exhibitor List - only when event is live */}
            {selectedEvent && selectedEvent.status === 'live' && (
              <div className="flex items-center gap-2">
                {listPricing[selectedEvent.id]?.exhibitor_list_purchased ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => downloadExhibitorList(selectedEvent.id, 'csv')}>
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadExhibitorList(selectedEvent.id, 'xlsx')}>
                      <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                    </Button>
                  </>
                ) : listPricing[selectedEvent.id]?.exhibitor_list_price > 0 ? (
                  <Button size="sm" variant="outline" onClick={() => handlePurchaseExhibitorList(selectedEvent.id)}>
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    {t('dashboard.buy_list')} ({formatPrice(listPricing[selectedEvent.id]?.exhibitor_list_price)})
                  </Button>
                ) : null}
              </div>
            )}
            {filteredExhibitors.length > 0 && selectedEvent?.status === 'live' && (
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('dashboard.search_exhibitors')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {myRegistrations.length === 0 ? (
          <div className="text-center py-8">
            <Store className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">{t('dashboard.register_events_desc')}</p>
            <Button onClick={() => setActiveTab('events')}>{t('dashboard.events')}</Button>
          </div>
        ) : selectedEvent && selectedEvent.status !== 'live' ? (
          <div className="text-center py-12" data-testid="exhibitors-locked-message">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
              <Lock className="h-8 w-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('event_detail.exhibitor_access_locked')}</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
              {t('event_detail.exhibitor_access_locked_desc')}
            </p>
            <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
              <Clock className="h-3 w-3 mr-1" /> {selectedEvent.status === 'registration_open' ? t('dashboard.reg_open_waiting') : selectedEvent.status?.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        ) : filteredExhibitors.length === 0 ? (
          <div className="text-center py-8">
            <Store className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t('dashboard.no_exhibitors_found')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredExhibitors.map((booth) => {
              const boothEvent = events.find(e => e.id === booth.event_id);
              return (
                <Card key={booth.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardContent className="p-0">
                    <div className="p-4">
                      {/* Exhibit/Show Name Badge */}
                      <div className="mb-2">
                        <Badge variant="outline" className="text-xs bg-primary/5 border-primary/20">
                          <Calendar className="h-3 w-3 mr-1" />
                          {boothEvent?.name || 'Unknown Exhibit'}
                        </Badge>
                      </div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{booth.company_name}</h3>
                          {booth.slogan && <p className="text-sm text-slate-500 italic">
                            <AutoTranslatedText text={booth.slogan} translations={booth.slogan_translations} showLoading={false} />
                          </p>}
                        </div>
                        <Badge className={
                          booth.tier === 'elite' ? 'bg-purple-100 text-purple-700' :
                          booth.tier === 'premium' ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {booth.tier === 'elite' ? t('event_detail.tier_elite') : booth.tier === 'premium' ? t('event_detail.tier_premium') : t('event_detail.tier_basic')}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        <AutoTranslatedText text={booth.description} translations={booth.description_translations} showLoading={false} />
                      </p>

                      {/* Seller Info */}
                      {booth.seller && (
                        <div className="bg-slate-50 rounded-md p-3 mb-3 text-sm">
                          <p className="font-medium">{booth.seller.name}</p>
                          {booth.seller.company_info && (
                            <div className="text-slate-500 mt-1 space-y-0.5">
                              <p><Globe className="inline h-3 w-3 mr-1" />{booth.seller.company_info.industry}</p>
                              <p>{booth.seller.company_info.city}, {booth.seller.company_info.country}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Products Preview */}
                      {booth.products?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-500 mb-2">{t('dashboard.products_services_label')}</p>
                          <div className="flex flex-wrap gap-1">
                            {booth.products.slice(0, 3).map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{p.name}</Badge>
                            ))}
                            {booth.products.length > 3 && (
                              <Badge variant="outline" className="text-xs">{t('dashboard.more_count', { count: booth.products.length - 3 })}</Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Rep Availability */}
                      <div className="flex items-center justify-between text-sm mb-4">
                        <span className="text-slate-500">
                          <Users className="inline h-4 w-4 mr-1" />
                          {t('dashboard.reps_available', { count: booth.active_reps_count || 0 })}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedBooth(booth); setShowBoothModal(true); }}
                        >
                          <Eye className="h-4 w-4 mr-1" /> {t('dashboard.more_info')}
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => { setSelectedBooth(booth); setInteractionType('call_request'); setShowInteractionModal(true); }}
                        >
                          <Video className="h-4 w-4 mr-1" /> {t('dashboard.talk_to_rep')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setMessageBooth(booth); setShowMessageModal(true); }}
                        >
                          <Send className="h-4 w-4 mr-1" /> {t('dashboard.messages')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuyerExhibitorsTab;
